import React, { useState } from "react";
import { Box, Button, Collapse } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LOCALIZATION } from "../../utils/localization";
import "./BlogContent.scss";

const ChadhavaTempleContent: React.FC<any> = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Box>
      <h1>Chadhava Temples – Offerings Made Simple with Temple Dekho</h1>

      <Collapse in={expanded} collapsedSize={100}>
        <p>
          In the sacred journey of devotion, Chadhava (offering) holds a special
          place — it is not just a ritual, but a heartfelt gesture of gratitude,
          surrender, and connection with the Divine. At{" "}
          <strong>Temple Dekho</strong>, we bring this timeless practice closer
          to you through our <strong>online Chadhava booking service</strong>,
          making it effortless to offer your devotion from anywhere in the
          world.
        </p>

        <p>
          In today’s fast-paced world, many devotees are unable to visit temples
          physically due to distance, work, or personal reasons. Yet, the desire
          to stay spiritually connected remains strong. Temple Dekho bridges
          this gap by allowing devotees to perform Chadhava and send offerings
          directly to temples with complete transparency and authenticity.
        </p>

        <h2>What is Chadhava?</h2>
        <p>
          Chadhava is the sacred act of offering — flowers, food, money, or
          other items — to a deity as a token of love, gratitude, or fulfillment
          of a vow. This age-old tradition symbolizes humility and devotion,
          expressing that everything we possess ultimately belongs to the
          Divine. Devotees can explore various{" "}
          <a href="/temples" className="linkedText">
            temples in India
          </a>{" "}
          where these sacred offerings are performed daily with deep reverence.
        </p>

        <p>
          Through Temple Dekho, you can now fulfill this spiritual duty without
          physical barriers. Each offering is performed under the supervision of
          temple priests, ensuring it follows proper vidhi (ritual process) and
          reaches the designated temple.
        </p>

        <h2>Our Mission: Making Sacred Giving Accessible</h2>
        <p>
          Temple Dekho’s mission is to simplify spirituality and ensure that no
          devotee is distanced from faith. With our Chadhava services, we
          provide a seamless way to participate in rituals, support temple
          upkeep, and contribute to the continuation of traditional worship
          practices — all with a few clicks. You can also{" "}
          <a href="/pujas" className="linkedText">
            book puja online
          </a>{" "}
          to accompany your Chadhava, extending your devotion through rituals
          performed at the temple.
        </p>

        <p>
          We combine <strong>tradition with technology </strong> to ensure your
          offerings are secure, authentic, and performed with complete
          reverence. For devotees who wish to witness the rituals live, Temple
          Dekho provides{" "}
          <a href="/livedarshan" className="linkedText">
            online darshan
          </a>{" "}
          services, allowing you to experience the divinity directly from your
          home.
        </p>

        <h2>How It Works</h2>
        <ol>
          <li>
            <strong>Select a Temple –</strong> Choose from our list of
            associated temples across India.
          </li>
          <li>
            <strong>Choose Your Offering –</strong> Flowers, Prasad, Deep Dan,
            temple fund, festival donation.
          </li>
          <li>
            <strong>Enter Your Details –</strong> Name, Gotra, family
            information for Sankalp.
          </li>
          <li>
            <strong>Make a Secure Payment –</strong> Using our trusted payment
            gateway.
          </li>
          <li>
            <strong>Receive Confirmation –</strong> Once the offering is
            completed, you’ll receive updates or Prasad delivery as applicable.
          </li>
        </ol>

        <h2>Why Offer Chadhava Online?</h2>
        <ul>
          <li>
            <strong> Authentic & Hassle-Free:</strong> Every offering is made at
            the temple through verified priests and partners.
          </li>
          <li>
            <strong>Accessible from Anywhere:</strong> Whether in India or
            abroad, your devotion travels seamlessly.
          </li>
          <li>
            <strong>Transparent Process: </strong> Temple Dekho ensures every
            step is clear, traceable, and genuine.
          </li>
          <li>
            <strong>Support Temples:</strong> Your contribution sustains temple
            maintenance, rituals, and community welfare.
          </li>
          <li>
            <strong>Fulfil Vows & Gratitude:</strong> Perform Chadhava on
            auspicious days, birthdays, anniversaries, or as thanksgiving for
            blessings received. With Temple Dekho, you can also include{" "}
            <a href="/prasadTemples" className="linkedText">
              online prasadam delivery
            </a>{" "}
            along with your offering, ensuring complete participation in temple
            rituals.
          </li>
        </ul>

        <h2>What You Can Offer</h2>
        <ul>
          <li>
            <strong>General Temple Donation –</strong> Contribute towards daily
            worship and temple maintenance.
          </li>
          <li>
            <strong>Festival Offerings –</strong> Participate in grand events
            like Navratri, Maha Shivratri, or Diwali.
          </li>
          <li>
            <strong>Specific Ritual Support –</strong> Sponsor aarti, prasad, or
            bhog on special occasions.
          </li>
          <li>
            <strong>Family or Ancestral Offering – </strong>Dedicate Chadhava in
            memory of ancestors or loved ones.
          </li>
        </ul>

        <h2>The Temple Dekho Promise</h2>
        <p>
          At Temple Dekho, every offering is performed as per authentic temple
          tradition. Our priests follow every ritual step with sincerity and
          devotion, ensuring <strong>your Chadhava is spiritually</strong>{" "}
          fulfilled and the blessings reach your home.
        </p>

        <p>
          Each temple listed on our platform is carefully verified, and the
          process is transparent at every stage — from selection to
          confirmation. You can even experience the divine energy through {" "} <a href="/livedarshan" className="linkedText">dev
          darshan</a>{" "} sessions while your Chadhava is being performed.
        </p>

        <h2>Book Your Chadhava Today</h2>
        <p>Embrace the essence of giving and gratitude.</p>
        <p>
          Offer your <a href="/chadhavaTemples" className="linkedText">online Chadhava</a> through Temple Dekho and stay connected
          with the divine, wherever you are.
        </p>
        <p>
          Let faith transcend distance — because when intention is pure,
          blessings always find their way home.
        </p>
      </Collapse>

      <Button
        variant="outlined"
        sx={{ mt: "10px" }}
        onClick={handleToggle}
        color="primary"
      >
        {expanded ? "Read Less" : t(LOCALIZATION.Read_More)}
      </Button>
    </Box>
  );
};

export default ChadhavaTempleContent;
